import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Flex, Grid, Link } from "theme-ui";

import { themevals } from "theme/themevals";
import Header from "components/ui/Header";
import Page from "components/ui/Page";
import BasicButton from "components/forms/BasicButton";
import { BackArrowIcon } from "components/svg";
import H2 from "components/typography/H2";
import P from "components/typography/P";
import Span from "components/typography/Span";
import Subtext from "components/typography/Subtext";
import TabSection from "containers/Clients/components/TabSection";
import { getLeadOptions, getLeadSettings, parseLeadCsv, importLeads, toDueDate, type ImportRow } from "api/Leads";

/**
 * Import leads from a CSV (s.14.4). Three steps:
 *   1. Choose the file (or paste rows) - it is checked straight away.
 *   2. Check: every row with what will happen to it. Rows that are already
 *      a CLIENT or a LEAD (same email, company email domain, website or
 *      name) are shown and never imported - a lead must not shadow a
 *      client. Choose owner, starting status and an optional next action.
 *   3. Import - sent in batches of 25 so a long list never hits the API
 *      gateway's 30-second limit; progress is shown; results listed.
 */

const BATCH = 25;

const input: React.CSSProperties = { padding: "8px 10px", border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: 6, fontSize: 13, fontFamily: "Poppins", width: "100%", boxSizing: "border-box", background: "#fff" };
const th: React.CSSProperties = { textAlign: "left", fontSize: 11, fontFamily: "Poppins", color: themevals.colors.dkgrey, fontWeight: 500, padding: "10px 14px", borderBottom: `1px solid ${themevals.colors.inputOutline}`, whiteSpace: "nowrap", background: "#fff", position: "sticky", top: 0 };
const td: React.CSSProperties = { fontSize: 13, fontFamily: "Poppins", padding: "10px 14px", borderBottom: `1px solid ${themevals.colors.inputOutline}`, verticalAlign: "top", color: themevals.colors.black };
const label: React.CSSProperties = { fontSize: 11, fontFamily: "Poppins", color: themevals.colors.dkgrey, display: "block", marginBottom: 4 };

type Filter = "all" | "ready" | "client" | "lead" | "other";

const kindOf = (r: ImportRow): Filter => (!r.problem ? "ready" : r.existing?.kind === "client" ? "client" : r.existing?.kind === "lead" ? "lead" : "other");

const ImportLeads = () => {
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [pasting, setPasting] = useState(false);
  const [csv, setCsv] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<ImportRow[] | null>(null);
  const [columns, setColumns] = useState<Record<string, string | null>>({});
  const [filter, setFilter] = useState<Filter>("all");
  const [owners, setOwners] = useState<Array<{ id: string; name: string }>>([]);
  const [ownerId, setOwnerId] = useState("");
  const [status, setStatus] = useState<"new" | "contacted" | "qualified">("new");
  const [nextNote, setNextNote] = useState("");
  const [nextAt, setNextAt] = useState("");
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [result, setResult] = useState<{ created: Array<{ id: string; name: string }>; skipped: Array<{ sourceRow: number; name: string; reason: string }> } | null>(null);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Import leads";
    getLeadOptions().then((o) => setOwners(o.owners));
    // Start from the company's default owner, if one is set.
    getLeadSettings().then((s) => { if (s.defaultOwnerId) setOwnerId(s.defaultOwnerId); });
  }, []);

  const check = async (text: string) => {
    if (!text.trim()) return;
    setBusy(true);
    setError(null);
    setResult(null);
    const res: any = await parseLeadCsv(text);
    setBusy(false);
    if (res?.error) return setError(res.error);
    setRows(res.rows);
    setColumns(res.columns);
    setFilter("all");
  };

  const takeFile = async (f?: File | null) => {
    if (!f) return;
    setFileName(`${f.name} \u00b7 ${Math.max(1, Math.round(f.size / 1024))} KB`);
    const text = await f.text();
    setCsv(text);
    check(text);
  };

  const counts = useMemo(() => {
    const c = { ready: 0, client: 0, lead: 0, other: 0 };
    for (const r of rows ?? []) c[kindOf(r) as keyof typeof c]++;
    return c;
  }, [rows]);
  const shown = useMemo(() => (rows ?? []).filter((r) => filter === "all" || kindOf(r) === filter), [rows, filter]);
  const mapped = Object.entries(columns).filter(([, v]) => v).map(([k, v]) => `${v} \u2192 ${k}`);

  const run = async () => {
    const ready = (rows ?? []).filter((r) => !r.problem);
    if (!ready.length) return;
    setBusy(true);
    setError(null);
    setProgress({ done: 0, total: ready.length });
    const created: Array<{ id: string; name: string }> = [];
    const skipped: Array<{ sourceRow: number; name: string; reason: string }> = [];
    const options = { leadStatus: status, nextActionAt: toDueDate(nextAt), nextActionNote: nextNote.trim() || null };
    for (let i = 0; i < ready.length; i += BATCH) {
      const res: any = await importLeads(ready.slice(i, i + BATCH), ownerId || null, options);
      if (res?.error) {
        setBusy(false);
        setProgress(null);
        setResult({ created, skipped });
        return setError(`${res.error} \u2014 stopped after ${created.length} imported. Import the file again to continue; already-imported rows will show as existing leads.`);
      }
      created.push(...res.created);
      skipped.push(...res.skipped);
      setProgress({ done: Math.min(i + BATCH, ready.length), total: ready.length });
    }
    setBusy(false);
    setProgress(null);
    setResult({ created, skipped });
  };

  const reset = () => { setRows(null); setResult(null); setCsv(""); setFileName(null); setPasting(false); setError(null); if (fileRef.current) fileRef.current.value = ""; };

  const Chip = ({ f, text, n }: { f: Filter; text: string; n: number }) => (
    <button
      type="button"
      onClick={() => setFilter(f)}
      style={{ padding: "5px 12px", borderRadius: 14, border: `1px solid ${filter === f ? themevals.colors.black : themevals.colors.inputOutline}`, background: filter === f ? themevals.colors.black : "#fff", color: filter === f ? "#fff" : themevals.colors.black, fontFamily: "Poppins", fontSize: 12, cursor: "pointer" }}
    >
      {text} ({n})
    </button>
  );

  return (
    <>
      <Header />
      <Page backAction={<Link variant="back" href="/leads"><BackArrowIcon />Back to Leads</Link>}>
        <H2>Import leads</H2>
        <P fontFamily="Poppins" fontSize={"13px"} color={themevals.colors.dkgrey} marginTop={"4px"} marginBottom={"20px"}>
          A CSV with one lead per row. Rows that are already a client or a lead are shown and left out, so nothing gets duplicated or overwritten.
        </P>
        {error && <Box sx={{ marginBottom: "16px", padding: "10px 14px", background: "#FBEAEA", color: "#A60000", borderRadius: "8px", fontFamily: "Poppins", fontSize: "13px" }}>{error}</Box>}

        {result ? (
          <Box sx={{ maxWidth: "760px" }}>
            <TabSection title="Import finished" bodyPadding>
              <P fontFamily="Poppins" fontSize={"15px"} fontWeight={600} marginTop={0} marginBottom={"8px"} color={themevals.colors.black}>
                {result.created.length} lead{result.created.length === 1 ? "" : "s"} imported{result.skipped.length ? `, ${result.skipped.length} left out` : ""}
              </P>
              {result.skipped.length > 0 && (
                <Box sx={{ marginBottom: "12px", maxHeight: "240px", overflowY: "auto" }}>
                  {result.skipped.map((s) => (
                    <Subtext key={s.sourceRow} fontSize={"12px"} fontFamily={"Poppins"} margin={0} color={themevals.colors.dkgrey}>
                      Row {s.sourceRow} {s.name ? `(${s.name})` : ""}: {s.reason}
                    </Subtext>
                  ))}
                </Box>
              )}
              <Flex sx={{ gap: "8px" }}>
                <BasicButton type="button" onClick={() => navigate("/leads")}>Open the leads list</BasicButton>
                <BasicButton scheme="secondaryOutline" type="button" keepText onClick={reset}>Import another file</BasicButton>
              </Flex>
            </TabSection>
          </Box>
        ) : !rows ? (
          <Box sx={{ maxWidth: "760px" }}>
            <TabSection title="1. Choose the file" subtitle="CSV or TSV; header row first" bodyPadding>
              <Box
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); takeFile(e.dataTransfer.files?.[0]); }}
                onClick={() => fileRef.current?.click()}
                sx={{ border: "2px dashed", borderColor: dragOver ? themevals.colors.black : "inputOutline", borderRadius: "8px", padding: "28px 16px", textAlign: "center", cursor: "pointer", background: dragOver ? "#FAFAF8" : "#fff", fontFamily: "Poppins" }}
              >
                <Box sx={{ fontSize: "14px", fontWeight: 600, color: themevals.colors.black }}>{busy ? "Checking the file\u2026" : fileName ?? "Drop a CSV here, or click to choose one"}</Box>
                <Box sx={{ fontSize: "12px", color: themevals.colors.dkgrey, marginTop: "4px" }}>
                  Columns are matched by name: Organization, Contact, Title, Email, Phone, Website, Address, City, State, Zip, Source, Notes.
                </Box>
                <input ref={fileRef} type="file" accept=".csv,.tsv,text/csv,text/tab-separated-values" style={{ display: "none" }} onChange={(e) => takeFile(e.target.files?.[0])} />
              </Box>

              <Box sx={{ marginTop: "12px" }}>
                <button type="button" onClick={() => setPasting((v) => !v)} style={{ background: "none", border: "none", padding: 0, color: "#185FA5", fontFamily: "Poppins", fontSize: 12, fontWeight: 500, cursor: "pointer" }}>
                  {pasting ? "Hide" : "Or paste the rows instead"}
                </button>
              </Box>
              {pasting && (
                <Box sx={{ marginTop: "8px" }}>
                  <textarea style={{ ...input, minHeight: 140, fontFamily: "monospace", fontSize: 12 }} value={csv} onChange={(e) => setCsv(e.target.value)} placeholder={"Organization,Contact,Email,Phone,City,State\nSummit Property Management,Dana Whitfield,dana@summit.com,(480) 555-0192,Gilbert,AZ"} />
                  <Box sx={{ marginTop: "10px" }}>
                    <BasicButton type="button" disabled={busy || !csv.trim()} $submitting={busy} onClick={() => check(csv)}>Check these rows</BasicButton>
                  </Box>
                </Box>
              )}
            </TabSection>
          </Box>
        ) : (
          <>
            <Grid gap={"16px"} columns={[2, 4]} sx={{ marginBottom: "16px" }}>
              <Stat label="Ready to import" value={counts.ready} tone="#27500A" />
              <Stat label="Already a client" value={counts.client} tone={counts.client ? "#633806" : undefined} />
              <Stat label="Already a lead" value={counts.lead} />
              <Stat label="Can't be imported" value={counts.other} tone={counts.other ? "#A60000" : undefined} />
            </Grid>

            <TabSection title="2. Choose how they arrive" subtitle="applies to every imported lead" bodyPadding>
              <Grid gap={"14px"} columns={[1, "1fr 1fr 2fr 170px"]}>
                <Box>
                  <span style={label}>Owner</span>
                  <select style={input} value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
                    <option value="">Me</option>
                    {owners.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                </Box>
                <Box>
                  <span style={label}>Starting status</span>
                  <select style={input} value={status} onChange={(e) => setStatus(e.target.value as any)}>
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="qualified">Qualified</option>
                  </select>
                </Box>
                <Box>
                  <span style={label}>Next action (optional)</span>
                  <input style={input} value={nextNote} onChange={(e) => setNextNote(e.target.value)} placeholder="e.g. Intro call from the Zoho list" />
                </Box>
                <Box>
                  <span style={label}>Due</span>
                  <input style={input} type="date" value={nextAt} onChange={(e) => setNextAt(e.target.value)} />
                </Box>
              </Grid>
              <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} marginTop={"8px"} color={themevals.colors.dkgrey}>
                Without a next action, imported leads sit at the bottom of the queue until someone gives them one. The Notes column becomes each lead&rsquo;s first note.
              </Subtext>
            </TabSection>

            <Box sx={{ marginTop: "20px" }}>
              <TabSection
                title="3. Check and import"
                subtitle={fileName ?? `${rows.length} rows`}
                action={
                  <Flex sx={{ gap: "8px", alignItems: "center" }}>
                    {progress && (
                      <Span fontFamily="Poppins" fontSize={"12px"} color={themevals.colors.dkgrey}>
                        Importing {progress.done} of {progress.total}…
                      </Span>
                    )}
                    <BasicButton scheme="secondaryOutline" type="button" keepText disabled={busy} onClick={reset}>Choose a different file</BasicButton>
                    <BasicButton type="button" disabled={busy || counts.ready === 0} $submitting={busy} onClick={run}>{`Import ${counts.ready} lead${counts.ready === 1 ? "" : "s"}`}</BasicButton>
                  </Flex>
                }
              >
                <Flex sx={{ gap: "6px", flexWrap: "wrap", padding: "12px 16px 0" }}>
                  <Chip f="all" text="All" n={rows.length} />
                  <Chip f="ready" text="Ready" n={counts.ready} />
                  <Chip f="client" text="Already a client" n={counts.client} />
                  <Chip f="lead" text="Already a lead" n={counts.lead} />
                  <Chip f="other" text="Can't be imported" n={counts.other} />
                </Flex>
                <Box sx={{ padding: "8px 16px 0", fontFamily: "Poppins", fontSize: "11px", color: themevals.colors.dkgrey }}>
                  Columns read: {mapped.length ? mapped.join(" \u00b7 ") : "none"}
                </Box>
                <Box sx={{ overflow: "auto", maxHeight: "60vh", marginTop: "8px" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={th}>Row</th><th style={th}>Organization</th><th style={th}>Contact</th><th style={th}>Email / phone</th><th style={th}>Location</th><th style={th}>Notes</th><th style={th}>What happens</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shown.map((r) => {
                        const k = kindOf(r);
                        const color = k === "ready" ? "#27500A" : k === "client" ? "#633806" : k === "lead" ? themevals.colors.dkgrey : "#A60000";
                        return (
                          <tr key={r.sourceRow} style={{ background: k === "ready" ? undefined : "#FFF8F6" }}>
                            <td style={td}>{r.sourceRow}</td>
                            <td style={{ ...td, fontWeight: 600 }}>{r.name || <Span color={themevals.colors.dkgrey}>{"\u2014"}</Span>}</td>
                            <td style={td}>{r.contactName}{r.contactTitle ? <div style={{ color: themevals.colors.dkgrey, fontSize: 12 }}>{r.contactTitle}</div> : null}</td>
                            <td style={td}>{r.contactEmail}{r.phone ? <div style={{ color: themevals.colors.dkgrey, fontSize: 12 }}>{r.phone}</div> : null}</td>
                            <td style={td}>{[r.address?.city, r.address?.state].filter(Boolean).join(", ")}</td>
                            <td style={{ ...td, color: themevals.colors.dkgrey, fontSize: 12 }}>{r.note ? `${r.note.split("\n").length} line${r.note.split("\n").length === 1 ? "" : "s"}` : ""}</td>
                            <td style={{ ...td, color, whiteSpace: "normal", minWidth: 220 }}>
                              {k === "ready" ? "Will be imported" : r.problem}
                              {r.existing && (
                                <div>
                                  <a
                                    href={r.existing.kind === "client" ? `/clients/${r.existing.id}` : `/leads/${r.existing.id}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ color: "#185FA5", fontSize: 12 }}
                                  >
                                    Open {r.existing.name}
                                  </a>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Box>
              </TabSection>
            </Box>
          </>
        )}
      </Page>
    </>
  );
};

const Stat: React.FC<{ label: string; value: any; tone?: string }> = ({ label, value, tone }) => (
  <Box sx={{ background: "#F1EFE8", borderRadius: "8px", padding: "14px 16px" }}>
    <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} color={themevals.colors.dkgrey}>{label}</Subtext>
    <Box sx={{ marginTop: "4px" }}>
      <Span fontFamily={"Poppins"} fontWeight={500} fontSize={"22px"} color={tone ?? themevals.colors.black}>{value}</Span>
    </Box>
  </Box>
);

export default ImportLeads;
